import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { doc, updateDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { UserContext } from './UserContext';
import OnboardingQuickstartModal from '../components/OnboardingQuickstartModal';

export const OnboardingQuickstartContext = createContext({
  openQuickstart: (_options) => {},
});

export const useOnboardingQuickstart = () => useContext(OnboardingQuickstartContext);

export const OnboardingQuickstartProvider = ({ children }) => {
  const { firestoreUser } = useContext(UserContext);
  const { pathname } = useLocation();
  const [showModal, setShowModal] = useState(false);
  const [page, setPage] = useState(1);
  const [loggedOutDashboard, setLoggedOutDashboard] = useState(false);
  // Prevents the effect from re-opening the modal immediately after dismissal
  // while the Firestore write propagates back through UserContext's onSnapshot.
  const justDismissedRef = useRef(false);

  useEffect(() => {
    if (!firestoreUser) return;
    // Already dismissed this session — don't re-open even if Firestore hasn't
    // confirmed onboardingModalSeen: true yet.
    if (justDismissedRef.current) return;
    // OnboardingStep5 sets `onboardingComplete: true` BEFORE navigating to
    // /onboarding-step-6 (steps 6 and 7 are optional bonus steps). Without
    // this route guard the modal would pop up immediately on step 6 instead
    // of waiting for the user's first dashboard visit. Suppress the modal
    // on any onboarding-step-* route; it will fire on the next route change
    // (typically when the user reaches the dashboard).
    if (pathname.startsWith('/onboarding-step-')) return;
    if (firestoreUser.onboardingComplete && !firestoreUser.onboardingModalSeen) {
      setPage(1);
      setShowModal(true);
    }
  }, [firestoreUser, pathname]);

  const dismissQuickstart = useCallback(async () => {
    // Flip the ref synchronously so the effect can't re-open the modal
    // before the Firestore write propagates back through UserContext.
    justDismissedRef.current = true;
    setShowModal(false);
    const user = auth.currentUser;
    if (user) {
      try {
        await updateDoc(doc(db, 'users', user.uid), { onboardingModalSeen: true });
      } catch (e) {
        console.error('Failed to mark onboarding modal seen:', e);
      }
    }
  }, []);

  const openQuickstart = useCallback(({ loggedOutDashboard: lod = false } = {}) => {
    setLoggedOutDashboard(lod);
    setPage(1);
    setShowModal(true);
  }, []);

  return (
    <OnboardingQuickstartContext.Provider value={{ openQuickstart }}>
      {children}
      {showModal && (
        <OnboardingQuickstartModal
          page={page}
          setPage={setPage}
          onClose={dismissQuickstart}
          loggedOutDashboard={loggedOutDashboard}
        />
      )}
    </OnboardingQuickstartContext.Provider>
  );
};
